import { ChartColumn, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import {
  useGetList,
  useLocaleState,
  useTranslate,
  type Identifier,
} from "ra-core";

import type { CrmCompany, Sale } from "../types";
import { formatEuros } from "./format";
import { AmountDelta, RenewalPill } from "./renewal";

const Chip = ({ children }: { children: React.ReactNode }) => (
  <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-border bg-card px-2.5 py-0.5 text-[13px] text-muted-foreground">
    {children}
  </span>
);

const saleName = (sale?: Sale) =>
  sale ? `${sale.first_name} ${sale.last_name}`.trim() : "";

/**
 * Company page header: what the rep used to dig for, at a glance. Owner and
 * collaborators, this year's contract against last year's, when it ends, the
 * renewal state, the last activity and the company page on construnario.com.
 */
export const CompanyHeader = ({ record }: { record: CrmCompany }) => {
  const translate = useTranslate();
  const [locale = "es"] = useLocaleState();
  const { data: sales } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 200 },
  });
  const byId = new Map<Identifier, Sale>((sales ?? []).map((s) => [s.id, s]));
  const owner = record.sales_id ? byId.get(record.sales_id) : undefined;
  const collaborators = (record.collaborators ?? [])
    .map((id) => saleName(byId.get(id)))
    .filter(Boolean);
  const year = new Date().getFullYear();

  const lastNote = (() => {
    if (!record.last_company_note_at) return null;
    const days = Math.round(
      (Date.now() - new Date(record.last_company_note_at).getTime()) /
        86_400_000,
    );
    return new Intl.RelativeTimeFormat(locale, { numeric: "auto" }).format(
      -days,
      "day",
    );
  })();

  const meta = [
    record.activity,
    [record.city, record.state_abbr].filter(Boolean).join(" · "),
    record.tax_identifier,
  ].filter(Boolean);

  return (
    <div className="flex flex-col gap-3">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">{record.name}</h1>
        {meta.length ? (
          <p className="text-sm text-muted-foreground">{meta.join(" · ")}</p>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {owner ? (
          <Chip>
            {translate("crm.construnario.header.owner")}
            <b className="font-semibold text-foreground">
              {saleName(owner)}
              {collaborators.length ? ` + ${collaborators.join(", ")}` : ""}
            </b>
          </Chip>
        ) : null}
        <Chip>
          {translate("crm.construnario.header.contract", { year })}
          <b className="font-semibold tabular-nums text-foreground">
            {formatEuros(record.contract_amount_current)}
          </b>
          <AmountDelta
            current={record.contract_amount_current}
            previous={record.contract_amount_previous}
          />
        </Chip>
        {record.contract_period_end_current ? (
          <Chip>
            {translate("crm.construnario.header.ends")}
            <b className="font-semibold tabular-nums text-foreground">
              {new Date(record.contract_period_end_current).toLocaleDateString(
                locale,
              )}
            </b>
          </Chip>
        ) : null}
        <RenewalPill status={record.renewal_status} />
        {lastNote ? (
          <Chip>
            {translate("crm.construnario.header.last_note")}
            <b className="font-semibold text-foreground">{lastNote}</b>
          </Chip>
        ) : null}
        {record.legacy_id ? (
          <Link
            to={`/companies/${record.id}/show/web`}
            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-0.5 text-[13px] text-accent-text no-underline hover:border-muted-foreground"
          >
            <ChartColumn className="size-3.5" />
            {translate("crm.construnario.header.web_stats")}
          </Link>
        ) : null}
        {record.construnario_url ? (
          <a
            href={record.construnario_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-0.5 text-[13px] text-accent-text no-underline hover:border-muted-foreground"
          >
            {translate("crm.construnario.portfolio.carp")}
            <ExternalLink className="size-3.5" />
          </a>
        ) : null}
      </div>
      {record.summary ? (
        <div className="rounded-lg border border-border bg-accent/40 px-3 py-2 text-sm text-muted-foreground">
          <span className="mb-0.5 block text-[11px] font-semibold uppercase tracking-[0.07em]">
            {translate("crm.construnario.header.summary")}
          </span>
          <span className="line-clamp-3">{record.summary}</span>
        </div>
      ) : null}
    </div>
  );
};
